import React, { useEffect, useMemo, useRef, useState } from "react";
import Drawer from "@elements/drawer";
import Button from "@elements/button";
import LeftArrow from "@svg/LeftArrow";
import { giftFinderApi } from "gate";
import { IGiftFinderPayloadQuestion, IGiftFinderQuestion } from "@types";
import QuestionStep from "./steps/QuestionStep";
import { useTranslations } from "next-intl";
import { useRouter } from "next/router";
import { toast } from "react-toastify";
import { useAppContext } from "@context/app";
import { segmentEvent } from "@utils/segment";

interface Props {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (payloadQuestions: IGiftFinderPayloadQuestion[]) => void | Promise<void>;
    fromPage?: string;
}

type AnswersMap = Record<string, string[]>;
type ExtraAnswersMap = Record<string, string>;

const getQuestionsList = (pages: any[] = []) => {
    const questions: IGiftFinderQuestion[] = [];
    for (const page of pages) {
        if (Array.isArray(page?.questions)) {
            questions.push(...page.questions);
        }
    }
    return questions;
};

const parseGiftFinderQuestionsParam = (
    queryParam: string | string[] | undefined
): IGiftFinderPayloadQuestion[] => {
    if (typeof queryParam !== "string") return [];

    try {
        const parsed = JSON.parse(queryParam);
        if (!Array.isArray(parsed)) return [];

        return parsed.reduce((acc, item) => {
            const questionId =
                typeof item?.id === "string" ? item.id.trim() : String(item?.id || "").trim();
            if (!questionId || !Array.isArray(item?.value)) return acc;

            acc.push({
                id: questionId,
                value: item.value.map((value: unknown) => String(value)),
                extra_question_value:
                    typeof item?.extra_question_value === "string"
                        ? item.extra_question_value
                        : undefined,
            });

            return acc;
        }, [] as IGiftFinderPayloadQuestion[]);
    } catch {
        return [];
    }
};

const mapPrefilledAnswers = (
    prefilledQuestions: IGiftFinderPayloadQuestion[],
    questions: IGiftFinderQuestion[]
) => {
    const mappedAnswers: AnswersMap = {};
    const mappedExtraAnswers: ExtraAnswersMap = {};
    const questionsMap = new Map(questions.map((question) => [question.id, question]));

    for (const questionAnswer of prefilledQuestions) {
        const questionId = questionAnswer?.id;
        const question = questionsMap.get(questionId);
        if (!question || !Array.isArray(questionAnswer?.value)) continue;

        const normalizedType = question.type.toLowerCase().replace(/[\s_]+/g, "");
        if (normalizedType.includes("text")) {
            const textValue = String(questionAnswer.value[0] || "").trim();
            if (textValue) {
                mappedAnswers[questionId] = [textValue];
            }
        } else {
            const validOptionIds = new Set(
                (question.options || []).map((option) => String(option.id))
            );
            const validAnswers = questionAnswer.value
                .map((value) => String(value))
                .filter((value) => validOptionIds.has(value));

            if (normalizedType.includes("single")) {
                if (validAnswers.length) {
                    mappedAnswers[questionId] = [validAnswers[0]];
                }
            } else if (validAnswers.length) {
                mappedAnswers[questionId] = [...new Set(validAnswers)];
            }
        }

        const trimmedExtraAnswer = questionAnswer.extra_question_value?.trim();
        if (trimmedExtraAnswer) {
            mappedExtraAnswers[questionId] = trimmedExtraAnswer;
        }
    }

    return {
        answers: mappedAnswers,
        extraAnswers: mappedExtraAnswers,
    };
};

const isQuestionAnswered = (question: IGiftFinderQuestion, answer: any[] | undefined) => {
    if (question.is_skippable) return true;
    if (!answer || !Array.isArray(answer) || !answer.length) return false;

    const normalizedType = question.type.toLowerCase().replace(/[\s_]+/g, "");
    if (normalizedType.includes("text")) {
        return Boolean(String(answer[0] || "").trim());
    }

    return true;
};

const GiftFinderDrawer: React.FC<Props> = ({
    isOpen,
    onClose,
    onSubmit,
    fromPage = "homepage",
}) => {
    const t = useTranslations();
    const router = useRouter();
    const { user, country, area, syncData } = useAppContext();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);
    const [questions, setQuestions] = useState<IGiftFinderQuestion[]>([]);
    const [answers, setAnswers] = useState<AnswersMap>({});
    const [extraAnswers, setExtraAnswers] = useState<ExtraAnswersMap>({});
    const [isLoading, setIsLoading] = useState(false);
    const [hasError, setHasError] = useState(false);
    const hasFetchedRef = useRef(false);
    const hasPrefilledRef = useRef(false);
    const isGiftFinderAllowed = syncData?.app_settings?.is_gift_finder_allowed;
    const language =
        typeof window !== "undefined" && window?.location?.pathname?.includes("/en")
            ? "English"
            : "Arabic";

    const currentQuestion = questions[currentStepIndex];
    const isLastStep = currentStepIndex === questions.length - 1;
    const canProceed = currentQuestion
        ? isQuestionAnswered(currentQuestion, answers[currentQuestion.id])
        : false;

    const payloadQuestions = useMemo(
        () =>
            Object.entries(answers).reduce((acc, [id, value]) => {
                if (!Array.isArray(value) || !value.length) return acc;
                acc.push({
                    id: id,
                    value,
                    extra_question_value: extraAnswers[id]?.trim() || undefined,
                });
                return acc;
            }, [] as IGiftFinderPayloadQuestion[]),
        [answers, extraAnswers]
    );

    const fetchQuestions = async () => {
        if (hasFetchedRef.current) return;
        setIsLoading(true);
        setHasError(false);
        try {
            const response = await giftFinderApi.getQuestions().fetch();
            if (response.meta.code === 200) {
                setQuestions(getQuestionsList(response.data.page));
                hasFetchedRef.current = true;
            }
        } catch (error) {
            console.error("Failed to load gift finder questions:", error);
            setHasError(true);
            toast.error(t("gift_finder_error"));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isGiftFinderAllowed || !isOpen) return;
        fetchQuestions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isGiftFinderAllowed, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            hasPrefilledRef.current = false;
            setCurrentStepIndex(0);
            setAnswers({});
            setExtraAnswers({});
            setHasError(false);
            return;
        }

        segmentEvent("Gift Finder Opened", {
            area_id: area?.id,
            country_id: country?.id,
            language,
            user_id: user?.data?.id || -1,
            from_page: fromPage,
        });
    }, [isOpen, user?.data?.id, country?.id, area?.id, language, fromPage]);

    useEffect(() => {
        if (!isOpen || isLoading || hasError || !questions.length || hasPrefilledRef.current) {
            return;
        }

        const parsedQuestions = parseGiftFinderQuestionsParam(router.query.gift_finder_questions);
        const prefilledData = mapPrefilledAnswers(parsedQuestions, questions);
        hasPrefilledRef.current = true;

        if (
            !Object.keys(prefilledData.answers).length &&
            !Object.keys(prefilledData.extraAnswers).length
        ) {
            return;
        }

        setAnswers(prefilledData.answers);
        setExtraAnswers(prefilledData.extraAnswers);
    }, [isOpen, isLoading, hasError, questions, router.query.gift_finder_questions]);

    const handleAnswerChange = (value: any[]) => {
        if (!currentQuestion) return;
        setAnswers((prev) => ({
            ...prev,
            [currentQuestion.id]: value,
        }));

        segmentEvent("Gift Finder Question Answered", {
            area_id: area?.id,
            country_id: country?.id,
            language,
            user_id: user?.data?.id || -1,
            question_id: currentQuestion.id,
            answer_value: value,
        });
    };

    const handleExtraAnswerChange = (value: string) => {
        if (!currentQuestion) return;
        setExtraAnswers((prev) => ({
            ...prev,
            [currentQuestion.id]: value,
        }));
    };

    const handleBack = () => {
        if (currentStepIndex === 0) {
            onClose();
            return;
        }
        setCurrentStepIndex((prev) => prev - 1);
    };

    const handleSkip = () => {
        if (!currentQuestion) return;
        segmentEvent("Gift Finder Question Skipped", {
            area_id: area?.id,
            country_id: country?.id,
            language,
            user_id: user?.data?.id || -1,
            question_id: currentQuestion.id,
        });

        if (isLastStep) return;
        setCurrentStepIndex((prev) => prev + 1);
    };

    const handleNext = async () => {
        if (!currentQuestion) return;
        if (!isQuestionAnswered(currentQuestion, answers[currentQuestion.id])) {
            toast.error(t("gift_finder_error"));
            return;
        }

        if (!isLastStep) {
            setCurrentStepIndex((prev) => prev + 1);
            return;
        }

        segmentEvent("Gift Finder Completed", {
            area_id: area?.id,
            country_id: country?.id,
            language,
            user_id: user?.data?.id || -1,
            questions_count: payloadQuestions.length,
        });

        onClose();
        await onSubmit(payloadQuestions);
    };

    if (!isGiftFinderAllowed) return null;

    return (
        <Drawer open={isOpen} onClose={onClose} cancelable={true} size="medium">
            <div className="p-4 border-b border-gray-100">
                <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2">
                        <button
                            type="button"
                            aria-label={t("gift_finder_back")}
                            onClick={handleBack}
                            className="cursor-pointer mr-3"
                        >
                            <LeftArrow className="rtl:rotate-180" />
                        </button>
                        <h1 className="text-base font-bold text-[#4B3C2A] uppercase">
                            {t("gift_finder_title")}
                        </h1>
                    </div>

                    {!isLoading && !hasError && currentQuestion?.is_skippable && !isLastStep ? (
                        <button
                            type="button"
                            onClick={handleSkip}
                            className="text-sm font-bold text-button-primary"
                        >
                            {t("gift_finder_skip")}
                        </button>
                    ) : null}
                </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4">
                {isLoading ? (
                    <p className="text-sm text-gray-500">{t("gift_finder_loading")}</p>
                ) : null}

                {!isLoading && hasError ? (
                    <div className="flex flex-col gap-4">
                        <p className="text-sm text-red-500">{t("gift_finder_error")}</p>
                        <Button
                            className="h-12 rounded-full w-full bg-button-primary"
                            onClick={() => {
                                hasFetchedRef.current = false;
                                fetchQuestions();
                            }}
                        >
                            {t("Retry")}
                        </Button>
                    </div>
                ) : null}

                {!isLoading && !hasError && currentQuestion ? (
                    <QuestionStep
                        question={currentQuestion}
                        answer={answers[currentQuestion.id] || []}
                        extraAnswer={extraAnswers[currentQuestion.id] || ""}
                        onAnswerChange={handleAnswerChange}
                        onExtraAnswerChange={handleExtraAnswerChange}
                    />
                ) : null}
            </div>

            {!isLoading && !hasError && currentQuestion ? (
                <div className="p-4 mt-auto border-t border-gray-100">
                    <div className="space-y-3 font-bold">
                        <Button
                            onClick={handleNext}
                            isDisabled={!canProceed}
                            className="w-full h-12 rounded-full bg-button-primary text-white"
                        >
                            {isLastStep ? t("gift_finder_find_gifts") : t("gift_finder_next")}
                        </Button>
                    </div>
                </div>
            ) : null}
        </Drawer>
    );
};

export default GiftFinderDrawer;
